import { Component, Vue } from 'vue-facing-decorator';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Check, Delete, List, CirclePlusFilled, Briefcase } from '@element-plus/icons-vue';
import Header from '@/components/Header/Header.vue';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.vue';
import Navigation from '@/components/Navigation/Navigation.vue';
import NavigationIndex from "@/models/EnumNavigationIndex";
import AuthService from '@/services/AuthService';
import ApiService from '@/services/ApiService';

@Component(
{
    components:
    {
        Header,
        Breadcrumbs,
        Navigation,
        List,
        Briefcase,
        CirclePlusFilled,
        Check,
        Delete
    },
    enums:
    {
        NavigationIndex
    }
})

export default class ClientEdit extends Vue 
{
    private activeName: string = "1";
    private clientId : string = '';
    private clientName : string = '';
    private hasPermission: boolean[] = [] as boolean[];
    private clientModel : ClientModel | undefined = {} as ClientModel;
    private loading : boolean = true;
    private loadingPassword : boolean = true;
    private formClientValid : boolean = true;
    private formPasswordValid: boolean = false;
    private oldPassword: string = '';
    private newPassword: string = '';
    private repeatPassword: string = '';
    private passwordForm: PasswordForm = {} as PasswordForm;
    private windowWidth : number = window.innerWidth;
    private windowHeight : number = window.innerHeight;
    private allowedPrefixChars : string = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

    private rulesClient =
    {
        name:
        [
            {
                required: true,
                message: "Bitte geben Sie eine Bezeichnung für dieses Amt ein.",
                trigger: "blur"
            }
        ],
        tokenprefix:
        [
            {
                required: true,
                message: "Bitte geben Sie ein Präfix für dieses Amt ein.",
                trigger: "blur"
            }
        ],
        email:
        [
            {
                required: true,
                message: "Bitte geben Sie eine gültige E-Mail für dieses Amt ein.",
                trigger: "blur"
            }
        ],
    };

    private rulesPassword =
    {
        newPassword:
        [
            {
                required: true,
                message: "Bitte geben Sie ein neues Passwort ein.",
                trigger: "blur"
            }
        ],
        repeatPassword:
        [
            {
                required: true,
                message: "Bitte wiederholen Sie das neue Passwort.",
                trigger: "blur"
            }
        ]
    };

    public async mounted()
    {
        // get permissions
        await AuthService.getPermissions();
        this.hasPermission['CLNT_SELECT'] = AuthService.hasPermission("CLNT_SELECT");
        this.hasPermission['CLNT_INSERT'] = AuthService.hasPermission("CLNT_INSERT");
        this.hasPermission['CLNT_UPDATE'] = AuthService.hasPermission("CLNT_UPDATE");
        this.hasPermission['CLNT_DELETE'] = AuthService.hasPermission("CLNT_DELETE");

        // get client id from get request
        this.clientId = this.$route.params.clientid as string;

        // load client from db
        this.clientModel = await ApiService.getClient(this.clientId);

        if (!this.clientModel) 
        {
            this.$router.push('/client');
            return;
        }
        else
        {
            this.clientName =  this.clientModel.name;

            window.addEventListener('resize', this.onWindowResize);

            this.loading = false;
            
            // check if a password has been set yet
            this.oldPassword = this.clientModel.password;
            this.loadingPassword = false;
        }
    }

    private beforeUnmount()
    {
        window.removeEventListener('resize', this.onWindowResize);
    }

	private onWindowResize(e : any)
    {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
    }

    private validateEmail(email : string)
    {
        let re: RegExp = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
        return re.test(email);
    }

    private validateClientForm() : boolean
    {
        if (!this.clientModel.name || this.clientModel.name.length <= 0) return false;
        if (!this.clientModel.email || this.clientModel.email.length <= 0) return false;
        if (!this.validateEmail(this.clientModel.email)) return false;

        if (!this.clientModel.tokenprefix || this.clientModel.tokenprefix.length < 2) return false;
        for (let c of this.clientModel.tokenprefix)
        {
            if (this.allowedPrefixChars.indexOf(c) < 0) return false;
        }

        return true;
    }

    private validatePasswordForm() : boolean
    {
        if (!this.passwordForm.newPassword || this.passwordForm.newPassword.length <= 0) return false;

        if (!this.passwordForm.repeatPassword || this.passwordForm.repeatPassword.length <= 0) return false;

        if (this.passwordForm.repeatPassword.length == this.passwordForm.newPassword.length)
        {
            if (this.passwordForm.repeatPassword !== this.passwordForm.newPassword)
            {
                ElMessage({message: "Die Passwörter stimmen nicht überein. Bitte prüfen Sie die Eingaben.", type: 'error'});
                return false;
            }
        }
        else
        {
            return false;
        }

        return true;
    }

    private async saveClient(client : ClientModel)
    {
        let ok : any = await ApiService.updateClient(client);

        if (ok)
        {
			ElMessage({message: "Änderungen wurden übernommen.", type: 'success'});
        }
        else
        {
            // or show error message
			ElMessage({message: "Änderungen konnten nicht gespeichert werden.",	type: 'error'});
        }
    }

    private onClientInput()
    {
        if (this.clientModel.tokenprefix)
        {
            this.clientModel.tokenprefix = this.clientModel.tokenprefix.toUpperCase();
        }
        this.formClientValid = this.validateClientForm();
    }

    private onClientSaveClick()
    {
        if (this.clientModel)
        {
            this.saveClient(this.clientModel);
        }
    }

    private async savePassword(client: ClientModel)
    {
        this.loadingPassword = true;

        let ok: any = await ApiService.patchClient(client);

        if (ok)
        {
            ElMessage({message: "Das neue Passwort wurde gespeichert.", type: 'success'});
            this.oldPassword = this.clientModel!.password;
        }
        else
        {
            // or show error message
            ElMessage({message: "Das neue Passwort konnte nicht gespeichert werden.", type: 'error'});
        }

        this.loadingPassword = false;
    }

    private onPasswordInput()
    {
        this.formPasswordValid = this.validatePasswordForm();
    }

    private onPasswordSaveClick()
    {
        if (this.clientModel)
        {
            this.clientModel.password = this.passwordForm.newPassword;
            this.savePassword(this.clientModel);
        }
    }
}
